import { COCO_PCM_CHUNK_MS, COCO_PCM_SAMPLE_RATE } from '../config/cocoVoice';

function downsampleFloat32(input, fromRate, toRate) {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const length = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(length);
  for (let i = 0; i < length; i += 1) {
    output[i] = input[Math.min(input.length - 1, Math.round(i * ratio))];
  }
  return output;
}

function float32ToInt16(float32) {
  const int16 = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, float32[i]));
    int16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return int16;
}

function concatInt16(a, b) {
  if (!a?.length) return b;
  if (!b?.length) return a;
  const merged = new Int16Array(a.length + b.length);
  merged.set(a, 0);
  merged.set(b, a.length);
  return merged;
}

/**
 * 采集麦克风并输出 16kHz mono int16 PCM，约每 100ms 回调一次。
 */
export async function createPcmCapture({ onChunk, onError } = {}) {
  const samplesPerChunk = Math.floor(COCO_PCM_SAMPLE_RATE * (COCO_PCM_CHUNK_MS / 1000));
  let stream = null;
  let audioContext = null;
  let processor = null;
  let source = null;
  let silentGain = null;
  let pending = new Int16Array(0);
  let muted = false;
  let running = false;

  const cleanup = async () => {
    running = false;
    if (processor) {
      processor.onaudioprocess = null;
      try {
        processor.disconnect();
      } catch {
        /* ignore */
      }
    }
    if (source) {
      try {
        source.disconnect();
      } catch {
        /* ignore */
      }
    }
    if (silentGain) {
      try {
        silentGain.disconnect();
      } catch {
        /* ignore */
      }
    }
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
    if (audioContext) {
      try {
        await audioContext.close();
      } catch {
        /* ignore */
      }
      audioContext = null;
    }
    processor = null;
    source = null;
    silentGain = null;
    pending = new Int16Array(0);
  };

  return {
    async start() {
      if (running) return;
      await cleanup();
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      audioContext = new AudioContext();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      source = audioContext.createMediaStreamSource(stream);
      processor = audioContext.createScriptProcessor(4096, 1, 1);
      silentGain = audioContext.createGain();
      silentGain.gain.value = 0;

      processor.onaudioprocess = (event) => {
        if (!running || muted) return;
        const input = event.inputBuffer.getChannelData(0);
        const down = downsampleFloat32(input, audioContext.sampleRate, COCO_PCM_SAMPLE_RATE);
        pending = concatInt16(pending, float32ToInt16(down));
        while (pending.length >= samplesPerChunk) {
          const chunk = pending.slice(0, samplesPerChunk);
          pending = pending.slice(samplesPerChunk);
          try {
            onChunk?.(chunk);
          } catch (e) {
            onError?.(e);
          }
        }
      };

      source.connect(processor);
      processor.connect(silentGain);
      silentGain.connect(audioContext.destination);
      running = true;
    },

    setMuted(nextMuted) {
      muted = !!nextMuted;
      if (muted) pending = new Int16Array(0);
    },

    isRunning() {
      return running;
    },

    stop: cleanup,
  };
}
